import React, { FC, useCallback, useMemo } from 'react';

import { MenuObjectTypes } from 'enums/menu';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useUIContext } from 'state/ui';

import { ModifierListItem } from './modifier-list-item';
import { IModifierTypeUIProps } from './types';
import {
  getModifierNonNumericOptionLabel,
  getModifierOptionMultiplier,
  getSingleSelectedOption,
  logProductModifierCustomization,
  transformSelectedOptionToSelections,
} from './utils';

export const ModifierNonNumeric: FC<IModifierTypeUIProps> = ({
  modifier,
  selectedItem,
  selections,
  onSelectionsChange,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { priceForItemOptionModifier } = useMenuContext();
  const { selectedProduct } = useProductWizardContext();
  const selectedOption = useMemo(() => getSingleSelectedOption(selections), [selections]);
  const allOptionsHavePrefix = useMemo(
    () => modifier.options.every(opt => !!opt.modifierMultiplier?.prefix),
    [modifier.options]
  );

  const handleSelect = useCallback(
    (val: string) => {
      const newSelectedOption = modifier.options.find(opt => opt._key === val) || selectedOption;
      onSelectionsChange(transformSelectedOptionToSelections(newSelectedOption));
      logProductModifierCustomization(
        newSelectedOption.name?.locale ?? '',
        modifier.name.locale,
        val
      );
    },
    [modifier.options, modifier.name.locale, selectedOption, onSelectionsChange]
  );

  const selectedProductItem = useMemo(() => {
    if (selectedProduct._type === MenuObjectTypes.COMBO) {
      if (selectedProduct.mainItem && !selectedProduct.mainItem.isDummyItem) {
        return selectedProduct.mainItem;
      } else if (selectedItem) {
        return selectedItem;
      }
    }
    return selectedProduct;
  }, [selectedItem, selectedProduct]);

  const radioListOptions = useMemo(() => {
    return modifier.options.map(opt => {
      const label = getModifierNonNumericOptionLabel(opt, allOptionsHavePrefix);
      const upchargePrice = priceForItemOptionModifier({
        item: selectedProductItem,
        itemOption: modifier,
        modifier: opt,
      });
      return {
        label: label || '',
        labelSuffix: upchargePrice > 0 ? `+${formatCurrencyForLocale(upchargePrice)}` : '',
        value: opt._key,
      };
    });
  }, [
    allOptionsHavePrefix,
    formatCurrencyForLocale,
    modifier,
    priceForItemOptionModifier,
    selectedProductItem,
  ]);

  const badgeLabel = allOptionsHavePrefix
    ? selectedOption.modifierMultiplier?.prefix?.locale
    : getModifierOptionMultiplier(selectedOption).toString();

  const selectedOptionIsZeroMultiplier = selectedOption.modifierMultiplier?.multiplier === 0;

  return (
    <>
      <ModifierListItem
        isSelected={
          (selectedOption.default && !selectedOptionIsZeroMultiplier) ||
          !selectedOptionIsZeroMultiplier
        }
        badgeContent={!selectedOptionIsZeroMultiplier ? badgeLabel : ''}
        content={modifier}
        selectedOption={selectedOption}
        options={radioListOptions}
        onOptionPress={handleSelect}
      />
    </>
  );
};
